import { Panel } from "primereact/panel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { useMediaQuery } from "react-responsive";
import studentsAPI from "../../../../services/students";

const StudentAttendancesPanel = ({
  studentId,
  schoolId,
  trainingId,
  className,
  header = "Absences / retards",
}) => {
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const attendancesQuery = studentsAPI.ListAttendances(schoolId, trainingId, "", studentId);
  const attendances = attendancesQuery?.data?.data || [];
  const attendancesTotal = attendancesQuery?.data?.total ?? attendances.length;

  const formatAttendanceDate = (isoDate) => {
    if (!isoDate) {
      return "-";
    }
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    const formattedDate = date.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Europe/Paris",
    });
    const formattedTime = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris",
    });
    return `${formattedDate} ${formattedTime}`;
  };

  const computeDelayMinutes = (delay) => {
    if (!delay && delay !== 0) {
      return null;
    }
    const numericDelay = Number(delay);
    if (!Number.isFinite(numericDelay) || numericDelay <= 0) {
      return null;
    }

    // By default the API returns nanoseconds (1 min = 60 * 1e9)
    let minutes = numericDelay / 60000000000;

    // Fallback to milliseconds (1 min = 60 * 1000)
    if (minutes < 0.5) {
      minutes = numericDelay / 60000;
    }

    // Fallback to seconds
    if (minutes < 0.5) {
      minutes = numericDelay / 60;
    }

    if (!Number.isFinite(minutes) || minutes <= 0) {
      return null;
    }

    return Math.round(minutes);
  };

  const formatDelay = (delay) => {
    const minutes = computeDelayMinutes(delay);
    if (minutes === null) {
      return "-";
    }
    return `${minutes} min`;
  };

  const getStatusSeverity = (status) => {
    switch (status) {
      case "ABSENT":
        return "danger";
      case "LATE":
        return "warning";
      default:
        return "info";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ABSENT":
        return "Absent";
      case "LATE":
        return "Retard";
      default:
        return status || "-";
    }
  };

  const renderTeacher = (rowData) => {
    const teacher = rowData?.course_session?.teacher?.member;
    if (!teacher) {
      return "-";
    }
    const nameParts = [teacher.firstname, teacher.lastname].filter(Boolean);
    return nameParts.length > 0 ? nameParts.join(" ") : "-";
  };

  const panelHeader = `${header} (${attendancesTotal})`;

  return (
    <Panel header={panelHeader} toggleable className={className}>
      {attendancesQuery?.isLoading && (
        <div className="py-4 text-center text-500">Chargement...</div>
      )}
      {attendancesQuery?.isError && (
        <div className="py-2 text-center text-red-500">
          {attendancesQuery?.error?.message || "Impossible de récupérer les absences et retards."}
        </div>
      )}
      {!attendancesQuery?.isLoading && !attendancesQuery?.isError && isMobile && (
        attendances.length === 0 ? (
          <div className="py-3 text-center text-500">Aucune absence ou retard enregistré.</div>
        ) : (
          <div className="flex flex-column gap-2" data-testid="attendances-cards">
            {attendances.map((rowData, index) => (
              <div
                key={rowData?.id || index}
                className="border-1 surface-border border-round p-3 flex flex-column gap-2"
                data-testid={`attendance-card-${rowData?.id || index}`}
              >
                <div className="flex justify-content-between align-items-center gap-2">
                  <Tag value={getStatusLabel(rowData?.status)} severity={getStatusSeverity(rowData?.status)} />
                  <small className="text-600 text-right">
                    {formatAttendanceDate(rowData?.course_session?.event?.start_date)}
                  </small>
                </div>
                {rowData?.status === "LATE" && formatDelay(rowData?.delay_time) !== "-" && (
                  <small className="text-700">Retard : {formatDelay(rowData?.delay_time)}</small>
                )}
                {rowData?.message && (
                  <div className="text-900" style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
                    {rowData.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
      {!attendancesQuery?.isLoading && !attendancesQuery?.isError && !isMobile && (
        <DataTable value={attendances} className="p-datatable-sm" emptyMessage="Aucune absence ou retard enregistré.">
          <Column
            header=""
            body={(rowData) => (
              <Tag
                value={getStatusLabel(rowData?.status)}
                severity={getStatusSeverity(rowData?.status)}
              />
            )}
          />
          <Column
            header="Séance"
            body={(rowData) => formatAttendanceDate(rowData?.course_session?.event?.start_date)}
          />
         
          <Column
            header="Message"
            body={(rowData) => rowData?.message || "-"}
          />
        </DataTable>
      )}
    </Panel>
  );
};

export default StudentAttendancesPanel;

